import { Constructor, Dictionary, RequireOnlyOne } from '@skylab-shared/types';
import { v4 as uuid } from 'uuid';
import { ConsoleLogger } from '../../logger';
import { Query, QueryBus } from './../../query';
import { CacheStorage } from './../../storage/src/cache-storage';

export interface ContextRegisterOptions {
    repeat: boolean;
}

export interface DataSourceRecord {
    dataProvider: DataProvider<any>;
    storageKey: string;
}

export type DataProvider<T> = () => Promise<T>;

export type Conditions<T> = { [K in keyof T]?: T[K] | RequireOnlyOne<ComplexFilter<T[K]>>; };
type ComplexFilter<T> = { in?: T[]; except?: T[]; };

export abstract class ContextBase {
    protected name?: string;

    protected readonly id: string;

    protected readonly storage: CacheStorage;
    protected readonly logger: ConsoleLogger;
    protected readonly queryBus: QueryBus;

    protected dataSourceRecords: DataSourceRecord[] = [];

    private readonly repeatableDataProvidersIds = new Set();

    public constructor(
        storage: CacheStorage,
        queryBus: QueryBus
    ) {
        this.id = uuid();

        this.storage = storage;
        this.queryBus = queryBus;
        this.logger = new ConsoleLogger(`Context`);
    }

    public async getAsync<T>(id: string): Promise<T>;
    public async getAsync<T>(query: Constructor<Query<T>>): Promise<T>;
    public async getAsync<T>(query: Constructor<Query<T>>, conditions: Conditions<T>): Promise<T>;
    public async getAsync<T>(queryOrId: Constructor<Query<T>> | string, conditions?: Conditions<T>): Promise<T | T[]> {
        const key = this.getStorageKey(queryOrId);
        if (!key) throw new Error(`Data source ${key} is not registered.`);

        const data = await this.getFromDataProviderAsync<T>(key);
        if (typeof conditions === 'object' && data instanceof Array) {
            const filter = this.createFilter(conditions);
            return this.applyFilter(data, filter);
        }

        return data;
    }

    public isDataProviderRegistered(id: string): boolean;
    public isDataProviderRegistered(query: Constructor<Query<any>>): boolean;
    public isDataProviderRegistered(queryOrId: any): boolean {
        const key = this.getStorageKey(queryOrId);
        return this.storage.has(key);
    }

    public async refresh<T>(id: string): Promise<T>;
    public async refresh<T>(query: Constructor<Query<T>>): Promise<T>;
    public async refresh<T>(queryOrId: any): Promise<T | T[]> {
        const key = this.getStorageKey(queryOrId);

        await this.storage.refresh(key);

        this.logger.info(`REFRESHED: ${key}`);

        return await this.getAsync<T>(queryOrId);
    }

    public async refreshAll(): Promise<void> {
        const promises = this.dataSourceRecords.map(ds => this.storage.refresh(ds.storageKey));
        await Promise.all(promises);
    }

    public dispose(): void {
        this.dataSourceRecords.forEach(ds => {
            this.storage.remove(ds.storageKey);
            this.logger.info(`REMOVED: ${ds.storageKey}.`);
        });
    }

    //#region Protected members
    protected async getFromDataProviderAsync<T>(id: string): Promise<T> {
        if (!this.isDataProviderRegistered(id))
            throw new Error(`Data source ${id} is not registered.`);

        return this.storage.getAsync<T>(id);
    }

    protected registerAsync<T>(id: string, dataProvider: DataProvider<T>, options?: ContextRegisterOptions): Promise<T | undefined>;
    protected registerAsync<T>(query: Constructor<Query<T>>, options?: ContextRegisterOptions): Promise<T | undefined>;
    protected registerAsync<T>(queryOrId: any, dataProviderOrOptions?: any, options?: ContextRegisterOptions): Promise<T | undefined> {
        const key = this.getStorageKey(queryOrId);
        if (dataProviderOrOptions?.repeat || options?.repeat) {
            this.repeatableDataProvidersIds.add(key);
        }

        let dataSourceRecord: DataSourceRecord;
        if (typeof queryOrId === 'string') {
            dataSourceRecord = {
                dataProvider: dataProviderOrOptions,
                storageKey: key
            };
        } else if (Query.prototype.isPrototypeOf(queryOrId.prototype)) {
            dataSourceRecord = {
                dataProvider: () => {
                    return this.queryBus.queryAsync(new queryOrId());
                },
                storageKey: key
            };
        } else {
            throw new Error(`Invalid registration target.`);
        }
        if (!this.getRepeatableDataProvidersIds().has(key) && this.isDataProviderRegistered(key))
            throw new Error(`Data source ${key} is already registered.`);

        this.dataSourceRecords.push(dataSourceRecord);

        return this.storage.addAsync(key, dataSourceRecord.dataProvider).then(() => {
            this.logger.info(`LOADED: ${key}.`, this.name);
            this.getRepeatableDataProvidersIds().delete(key);
            return this.storage.get<T>(key);
        }).catch(_ => {
            if (this.getRepeatableDataProvidersIds().has(key)) {
                this.logger.warn(`First Load ${key} data gone wrong, but load will be repeat`);
                return undefined;
            }
        });
    }

    protected getUniqueStorageKey(): string {
        return `${this.id}#${uuid()}`;
    }

    protected createFilter<T>(conditions: Conditions<T>): (item: T) => boolean {
        const isComplexFilter = (condition: ComplexFilter<unknown> | unknown): condition is ComplexFilter<unknown> =>
            typeof condition === 'object' && condition !== null;

        const satisfyCondition = (value: unknown, condition: ComplexFilter<unknown> | unknown): boolean => {
            if (isComplexFilter(condition)) {
                if ('in' in condition && Array.isArray(condition.in)) {
                    return condition.in.includes(value);
                }
                if ('except' in condition && Array.isArray(condition.except)) {
                    return !condition.except.includes(value);
                }
                return true;
            }
            return value === condition;
        };

        return (item: T) => {
            const condDict = conditions as Dictionary<unknown>;
            return Object.keys(condDict).every(prop => satisfyCondition((item as any)[prop], condDict[prop]));
        };
    }

    protected applyFilter<T>(data: T[], filter: (item: T) => boolean): T[] {
        return data.filter(filter);
    }

    protected getStorageKey(queryOrId: any): string {
        if (typeof queryOrId === 'string') {
            return queryOrId;
        } else if (Query.prototype.isPrototypeOf(queryOrId.prototype)) {
            return queryOrId.name;
        }
        return '';
    }

    protected getRepeatableDataProvidersIds(): Set<unknown> {
        return this.repeatableDataProvidersIds;
    }

    //#endregion
}
